// Wrappers for the tutor-side BMD report endpoints, mounted under
// `/bmdReport/*` in tutor-backend.
//
// Use the shared `apiClient` from `src/infra/api-client.ts`, not raw axios:
// it handles Bearer-token injection and the expired-token redirect.

import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  GetMyStudentsForReportResponse,
  GetStudentPrereqGraphRequest,
  GetStudentPrereqGraphResponse,
  GetStudentReportRequest,
  GetStudentReportResponse,
  GetStudentSkillsGapRequest,
  GetStudentSkillsGapResponse,
  GetTopicAttemptDetailRequest,
  GetTopicAttemptDetailResponse,
} from "../types/bmd-report.types"

const ENDPOINTS = {
  getMyStudentsForReport: "bmdReport/getMyStudentsForReport",
  getStudentReport: "bmdReport/getStudentReport",
  getStudentTopicAttemptDetail: "bmdReport/getStudentTopicAttemptDetail",
  getStudentSkillsGap: "bmdReport/getStudentSkillsGap",
  getStudentPrereqGraph: "bmdReport/getStudentPrereqGraph",
} as const

export async function getMyStudentsForReport(
  signal?: AbortSignal,
): Promise<GetMyStudentsForReportResponse> {
  const response = await apiClient.post<GetMyStudentsForReportResponse>(
    BASE_URL + ENDPOINTS.getMyStudentsForReport,
    {},
    { signal },
  )
  const data = response.data
  return {
    students: Array.isArray(data?.students) ? data.students : [],
  }
}

export async function getStudentReport(
  req: GetStudentReportRequest,
  signal?: AbortSignal,
): Promise<GetStudentReportResponse> {
  const response = await apiClient.post<GetStudentReportResponse>(
    BASE_URL + ENDPOINTS.getStudentReport,
    {
      student_key: req.student_key,
      year_level_id: req.year_level_id,
      // Omitted for whole-year-level scope. The backend reads an absent key
      // as "everything", which is how it behaved before the toggle existed.
      ...(req.diagnostics_session_key
        ? { diagnostics_session_key: req.diagnostics_session_key }
        : {}),
    },
    { signal },
  )
  return {
    ...response.data,
    available_sessions: Array.isArray(response.data?.available_sessions)
      ? response.data.available_sessions
      : [],
    selected_session_key: response.data?.selected_session_key ?? null,
  }
}

export async function getStudentTopicAttemptDetail(
  req: GetTopicAttemptDetailRequest,
  signal?: AbortSignal,
): Promise<GetTopicAttemptDetailResponse> {
  const response = await apiClient.post<GetTopicAttemptDetailResponse>(
    BASE_URL + ENDPOINTS.getStudentTopicAttemptDetail,
    {
      session_topic_key: req.session_topic_key,
    },
    { signal },
  )
  return response.data
}

export async function getStudentSkillsGap(
  req: GetStudentSkillsGapRequest,
  signal?: AbortSignal,
): Promise<GetStudentSkillsGapResponse> {
  const response = await apiClient.post<GetStudentSkillsGapResponse>(
    BASE_URL + ENDPOINTS.getStudentSkillsGap,
    {
      student_key: req.student_key,
      year_level_id: req.year_level_id,
      ...(req.diagnostics_session_key
        ? { diagnostics_session_key: req.diagnostics_session_key }
        : {}),
      // Must be forwarded. This body is built field by field rather than from
      // a spread, so a new field on the request type reaches the server only
      // if it is named here - `scope` was added and not, which left the table
      // on the base test while the strip above it had switched to overall.
      ...(req.scope ? { scope: req.scope } : {}),
    },
    { signal },
  )
  return response.data
}

export async function getStudentPrereqGraph(
  req: GetStudentPrereqGraphRequest,
  signal?: AbortSignal,
): Promise<GetStudentPrereqGraphResponse> {
  const response = await apiClient.post<GetStudentPrereqGraphResponse>(
    BASE_URL + ENDPOINTS.getStudentPrereqGraph,
    {
      student_key: req.student_key,
      strand_id: req.strand_id ?? null,
      subject_id: req.subject_id,
      year_level_id: req.year_level_id ?? null,
    },
    { signal },
  )
  return response.data
}
